import { useMemo, useState } from 'react'

import { CatalogUnitOfMeasure, Product, UnitOfMeasure } from '@/api/admin-products-types'
import { useCreateProductUnitOfMeasure, useUpdateProductUnitOfMeasure } from '@/api/api-mutations'
import { useApiContext } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'
import { useAsyncEffect } from '@/utils/hooks'
import { sortArray } from '@/utils/utilities'

export function UnitOfMeasureForm({ product, unit }: { product: Product; unit?: UnitOfMeasure }) {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const { api } = useApiContext()
	const createUnit = useCreateProductUnitOfMeasure()
	const updateUnit = useUpdateProductUnitOfMeasure()
	const productUuid = product.uuid

	const [catalog, setCatalog] = useState<CatalogUnitOfMeasure[]>([])
	const [loading, setLoading] = useState(!unit)

	// Catalogue units not yet on this variant (and not its base) are the ones that can be added.
	const existingUuids = useMemo(
		() => new Set((product.variants[0]?.unitsOfMeasure ?? []).map(candidate => candidate.uuid)),
		[product]
	)

	useAsyncEffect(async () => {
		if (unit) {
			return
		}
		try {
			const units = await api.listUnitsOfMeasure({})
			setCatalog(
				sortArray(
					units.filter(candidate => !existingUuids.has(candidate.uuid)),
					x => x.code
				)
			)
		} finally {
			setLoading(false)
		}
	}, [])

	const options = useMemo(
		() =>
			catalog.map(candidate => ({
				value: candidate.uuid,
				label: candidate.name ? `${candidate.code} — ${candidate.name}` : candidate.code
			})),
		[catalog]
	)

	const form = useAppForm({
		defaultValues: {
			unitOfMeasureUuid: unit?.uuid ?? '',
			// Seed only the raw per-link override, never the (possibly inherited) effective quantity.
			// Otherwise re-saving without touching the quantity would submit the inherited catalogue
			// quantity and silently freeze it as an override on this link.
			conversionFactorOverride:
				unit?.conversionFactorOverride != null ? String(unit.conversionFactorOverride) : ''
		},
		onSubmit: async ({ value }) => {
			if (!value.unitOfMeasureUuid) {
				toast.error(L.Product.UnitsOfMeasure.SelectUnit())
				return
			}
			const trimmed = value.conversionFactorOverride.trim()
			let conversionFactorOverride: number | undefined
			if (trimmed !== '') {
				const parsed = Number(trimmed)
				if (!(parsed > 0)) {
					toast.error(L.Product.UnitsOfMeasure.InvalidFactor())
					return
				}
				conversionFactorOverride = parsed
			} else {
				// No override: the selected catalogue unit must carry a shared quantity.
				const selected = catalog.find(candidate => candidate.uuid === value.unitOfMeasureUuid)
				if (!unit && selected && selected.conversionFactor == null) {
					toast.error(L.Product.UnitsOfMeasure.QuantityRequired())
					return
				}
			}
			const body = {
				unitOfMeasureUuid: value.unitOfMeasureUuid,
				conversionFactorOverride
			}
			const result = unit
				? await updateUnit({ path: { productUuid, unitUuid: unit.uuid }, body })
				: await createUnit({ path: { productUuid }, body })
			if (result.isSuccess) {
				toast.success(L.Product.UnitsOfMeasure.Saved())
				handleSuccess()
			} else {
				toast.error((result.response as { message?: string })?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{unit ? L.Product.UnitsOfMeasure.Edit() : L.Product.UnitsOfMeasure.Add()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						{unit ? (
							<form.AppField
								name='unitOfMeasureUuid'
								children={() => (
									<div className='flex flex-col gap-y-1'>
										<span className='text-xs text-ui-fg-subtle'>
											{L.Product.UnitsOfMeasure.Unit()}
										</span>
										<span className='txt-compact-medium text-ui-fg-base'>
											{unit.name ? `${unit.code} — ${unit.name}` : unit.code}
										</span>
									</div>
								)}
							/>
						) : (
							<form.AppField
								name='unitOfMeasureUuid'
								children={field => (
									<field.SelectField
										label={L.Product.UnitsOfMeasure.Unit()}
										options={options}
										optionsLoading={loading}
									/>
								)}
							/>
						)}
						<form.AppField
							name='conversionFactorOverride'
							children={field => (
								<field.TextField label={L.Product.UnitsOfMeasure.BaseUnitsPer()} optional />
							)}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
